import { useCallback, useEffect, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import PasswordInput from "../../components/PasswordInput";
import { api, formatApiErrorDetail } from "../../lib/api";

/**
 * AdminSecurityPage — admin-only 2FA TOTP enrollment.
 *
 * Three states:
 *   - loading           (initial fetch of status)
 *   - disabled / enroll (show "Start enrollment" CTA; after start, show QR)
 *   - enabled           (show "Disable" form requiring password)
 */
export default function AdminSecurityPage() {
  const [status, setStatus] = useState(null);
  const [enrollment, setEnrollment] = useState(null); // {secret, otpauth_url, qr_data_uri}
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const startEnroll = async () => {
    setError("");
    setSuccess("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll/start");
      setEnrollment(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e) => {
    e?.preventDefault?.();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/2fa/enroll/confirm", { code });
      setEnrollment(null);
      setCode("");
      setSuccess("Autenticazione a due fattori attivata.");
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e?.preventDefault?.();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password });
      setPassword("");
      setSuccess("Autenticazione a due fattori disattivata.");
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PortalLayout>
      <section
        className="max-w-[800px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="admin-security-page"
      >
        <div className="br-fade-up">
          <div className="br-overline">Sicurezza account</div>
          <h1 className="font-serif-display text-[clamp(2.2rem,4vw,3.2rem)] mt-3">
            Autenticazione a <em>due fattori</em>.
          </h1>
          <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-3 max-w-[640px]">
            Aggiunge un secondo livello di protezione richiedendo un codice
            TOTP a 6 cifre generato dalla tua app Authenticator a ogni login.
            Compatibile con Google Authenticator, Authy, 1Password, Bitwarden.
          </p>
        </div>

        {error && (
          <div
            className="mt-6 br-card p-4 text-[color:var(--br-red)]"
            data-testid="security-error"
          >
            {String(error)}
          </div>
        )}
        {success && (
          <div
            className="mt-6 br-card p-4"
            style={{ color: "var(--br-bronze-soft)" }}
            data-testid="security-success"
          >
            ✓ {success}
          </div>
        )}

        <div className="mt-8 br-card p-7 br-fade-up br-delay-1" data-testid="twofa-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="br-overline">Stato corrente</div>
            <span
              className="font-mono-fin text-[0.66rem] tracking-[0.22em] uppercase border px-3 py-1"
              style={{
                color: status?.enabled ? "var(--br-bronze-soft)" : "var(--br-text-dim)",
                borderColor: status?.enabled ? "var(--br-bronze-soft)" : "var(--br-line-bright)",
              }}
              data-testid="twofa-state-badge"
            >
              {status?.enabled ? "Attivo" : "Disattivato"}
            </span>
          </div>

          {!status?.enabled && !enrollment && (
            <div className="mt-6">
              <p className="text-[0.92rem] text-[color:var(--br-text)] leading-relaxed">
                Attiva l&apos;autenticazione a due fattori per il tuo account admin.
                È fortemente consigliata per gli account con privilegi di gestione clienti.
              </p>
              <button
                onClick={startEnroll}
                disabled={busy}
                className="br-btn-primary mt-5"
                data-testid="twofa-start-btn"
              >
                {busy ? "Generazione…" : "Avvia configurazione"}
              </button>
            </div>
          )}

          {!status?.enabled && enrollment && (
            <form onSubmit={confirmEnroll} className="mt-6 space-y-5" data-testid="twofa-enroll-form">
              <div>
                <div className="br-overline mb-3">1. Scansiona il QR code</div>
                <div
                  className="inline-block border p-3 bg-[color:var(--br-parchment)]"
                  style={{ borderColor: "var(--br-bronze-soft)" }}
                  data-testid="twofa-qr-wrapper"
                >
                  <img
                    src={enrollment.qr_data_uri}
                    alt="2FA QR code"
                    width={192}
                    height={192}
                    data-testid="twofa-qr-image"
                  />
                </div>
                <p className="text-[0.84rem] text-[color:var(--br-text-dim)] mt-3 leading-relaxed">
                  Apri Google Authenticator (o equivalente) → &quot;+&quot; → Scansiona codice QR. In alternativa,
                  inserisci manualmente la chiave segreta:
                </p>
                <code
                  className="block mt-2 font-mono-fin text-[0.84rem] px-3 py-2 break-all"
                  style={{
                    background: "rgba(167,128,73,0.06)",
                    borderLeft: "2px solid var(--br-bronze)",
                    color: "var(--br-parchment)",
                  }}
                  data-testid="twofa-secret"
                >
                  {enrollment.secret}
                </code>
              </div>

              <label className="block">
                <span className="br-overline mb-3 block">2. Inserisci il codice generato</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="br-input font-mono-fin tracking-[0.4em] text-center text-[1.4rem]"
                  autoComplete="one-time-code"
                  data-testid="twofa-confirm-code"
                  required
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEnrollment(null);
                    setCode("");
                  }}
                  className="br-btn-outline"
                  disabled={busy}
                  data-testid="twofa-cancel"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="br-btn-primary"
                  disabled={busy || code.length !== 6}
                  data-testid="twofa-confirm-btn"
                >
                  {busy ? "Verifica…" : "Attiva 2FA"}
                </button>
              </div>
            </form>
          )}

          {status?.enabled && (
            <form onSubmit={disable} className="mt-6 space-y-5" data-testid="twofa-disable-form">
              <p className="text-[0.92rem] text-[color:var(--br-text)] leading-relaxed">
                Per disattivare la 2FA conferma la tua password attuale.
              </p>
              <label className="block">
                <span className="br-overline mb-3 block">Password attuale</span>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="twofa-disable-password"
                  required
                />
              </label>
              <button
                type="submit"
                className="br-btn-outline"
                style={{ borderColor: "var(--br-red)", color: "var(--br-red)" }}
                disabled={busy || !password}
                data-testid="twofa-disable-btn"
              >
                {busy ? "Disattivazione…" : "Disattiva 2FA"}
              </button>
            </form>
          )}
        </div>
      </section>
    </PortalLayout>
  );
}
